import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/shared/Layout';
import { auditAPI } from '../services/api';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  Shield, Search, X, ChevronLeft, ChevronRight,
  LogIn, UserPlus, UserCog, Palmtree, ClipboardList,
  Building2, Settings, Lock, KeyRound,
} from 'lucide-react';

const PAGE_SIZE = 50;

// Icono y color por acción
const ACTION_CONFIG = {
  LOGIN:                   { Icon: LogIn,         color: '#2563eb', bg: '#eff6ff' },
  CHANGE_PASSWORD:         { Icon: Lock,          color: '#7c3aed', bg: '#f5f3ff' },
  RESET_PASSWORD:          { Icon: KeyRound,      color: '#d97706', bg: '#fffbeb' },
  CREATE_EMPLOYEE:         { Icon: UserPlus,      color: '#16a34a', bg: '#f0fdf4' },
  UPDATE_EMPLOYEE:         { Icon: UserCog,       color: '#0891b2', bg: '#ecfeff' },
  UPDATE_PROFILE:          { Icon: UserCog,       color: '#0891b2', bg: '#ecfeff' },
  TOGGLE_EMPLOYEE_ACTIVE:  { Icon: UserCog,       color: '#d97706', bg: '#fffbeb' },
  UPDATE_SUPERVISOR:       { Icon: UserCog,       color: '#0891b2', bg: '#ecfeff' },
  PROMOTE_TO_SUPERVISOR:   { Icon: Shield,        color: '#7c3aed', bg: '#f5f3ff' },
  REQUEST_VACATION:        { Icon: Palmtree,      color: '#16a34a', bg: '#f0fdf4' },
  CANCEL_VACATION:         { Icon: Palmtree,      color: '#dc2626', bg: '#fef2f2' },
  PROCESS_VACATION:        { Icon: Palmtree,      color: '#2563eb', bg: '#eff6ff' },
  REQUEST_LEAVE:           { Icon: ClipboardList, color: '#16a34a', bg: '#f0fdf4' },
  CANCEL_LEAVE:            { Icon: ClipboardList, color: '#dc2626', bg: '#fef2f2' },
  PROCESS_LEAVE:           { Icon: ClipboardList, color: '#2563eb', bg: '#eff6ff' },
  CREATE_DEPARTMENT:       { Icon: Building2,     color: '#16a34a', bg: '#f0fdf4' },
  UPDATE_DEPARTMENT:       { Icon: Building2,     color: '#0891b2', bg: '#ecfeff' },
  DELETE_DEPARTMENT:       { Icon: Building2,     color: '#dc2626', bg: '#fef2f2' },
  TRIGGER_ANNIVERSARIES:   { Icon: Settings,      color: '#7c3aed', bg: '#f5f3ff' },
  RECALCULATE_VACATIONS:   { Icon: Settings,      color: '#7c3aed', bg: '#f5f3ff' },
};

const ENTITY_OPTIONS = [
  { value: 'all',        label: 'Todas las entidades' },
  { value: 'user',       label: 'Usuario' },
  { value: 'employee',   label: 'Empleado' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'vacation',   label: 'Vacaciones' },
  { value: 'leave',      label: 'Permisos' },
  { value: 'department', label: 'Departamento' },
  { value: 'system',     label: 'Sistema' },
];

const ROLE_LABELS = { admin: 'Admin', supervisor: 'Supervisor', employee: 'Empleado' };
const ROLE_COLORS = {
  admin:      { color: '#7c3aed', bg: '#f5f3ff' },
  supervisor: { color: '#2563eb', bg: '#eff6ff' },
  employee:   { color: '#16a34a', bg: '#f0fdf4' },
};

export default function AuditPage() {
  const [logs,    setLogs]    = useState([]);
  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page,    setPage]    = useState(0);
  const [searched, setSearched] = useState(false);

  const [filters, setFilters] = useState({
    date_from: '', date_to: '', user_id: 'all',
    action: 'all', entity: 'all', search: '',
  });

  useEffect(() => {
    auditAPI.getUsers().then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const handleSearch = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: p * PAGE_SIZE };
      if (filters.date_from)           params.date_from = filters.date_from;
      if (filters.date_to)             params.date_to   = filters.date_to;
      if (filters.user_id !== 'all')   params.user_id   = filters.user_id;
      if (filters.action !== 'all')    params.action    = filters.action;
      if (filters.entity !== 'all')    params.entity    = filters.entity;
      if (filters.search.trim())       params.search    = filters.search.trim();

      const res = await auditAPI.getLogs(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPage(p);
      setSearched(true);
    } catch {
      toast.error('Error al cargar el audit trail');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Cargar al entrar
  useEffect(() => { handleSearch(0); }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const f = (key) => (e) => setFilters(p => ({ ...p, [key]: e.target.value }));

  const handleReset = () => {
    setFilters({ date_from:'', date_to:'', user_id:'all', action:'all', entity:'all', search:'' });
  };

  return (
    <Layout title="Audit Trail">
      <div className="page-header">
        <div>
          <div className="page-title" style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Shield size={22} color="var(--primary)" strokeWidth={1.8} />
            Audit Trail
          </div>
          <div className="page-subtitle">
            Registro completo de todas las acciones realizadas en el sistema
            {total > 0 && <span style={{ marginLeft:8, fontWeight:600, color:'var(--primary)' }}>· {total.toLocaleString()} registros</span>}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card section-gap">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Desde</label>
            <input type="date" className="form-input" value={filters.date_from} onChange={f('date_from')} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Hasta</label>
            <input type="date" className="form-input"
            value={filters.date_to} min={filters.date_from || undefined} onChange={f('date_to')} />
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Usuario</label>
            <select className="form-input form-select" value={filters.user_id} onChange={f('user_id')}>
              <option value="all">Todos los usuarios</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.user_email} ({ROLE_LABELS[u.user_role] || u.user_role})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin:0 }}>
            <label className="form-label">Entidad</label>
            <select className="form-input form-select" value={filters.entity} onChange={f('entity')}>
              {ENTITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin:0, gridColumn:'span 2' }}>
            <label className="form-label">Buscar</label>
            <input className="form-input" placeholder="Buscar por email, descripción..."
              value={filters.search} onChange={f('search')}
              onKeyDown={e => e.key === 'Enter' && handleSearch(0)} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:14, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={handleReset}
            style={{ display:'flex', alignItems:'center', gap:6 }}>
            <X size={14} /> Limpiar
          </button>
          <button className="btn btn-primary" onClick={() => handleSearch(0)} disabled={loading}
            style={{ display:'flex', alignItems:'center', gap:6 }}>
            <Search size={14} /> {loading ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        {logs.length === 0 && !loading ? (
          <div className="empty-state">
            <Shield size={40} color="var(--gray-300)" style={{ margin:'0 auto 12px', display:'block' }} />
            <div className="empty-state-title">Sin registros</div>
            <div style={{ fontSize:13 }}>No hay actividad para los filtros seleccionados</div>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Fecha y hora</th>
                    <th>Usuario</th>
                    <th>Rol</th>
                    <th>Acción</th>
                    <th>Descripción</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => {
                    const cfg = ACTION_CONFIG[log.action] || { Icon: Settings, color:'var(--gray-500)', bg:'var(--gray-100)' };
                    const Icon = cfg.Icon;
                    const roleStyle = ROLE_COLORS[log.user_role] || { color:'var(--gray-600)', bg:'var(--gray-100)' };
                    return (
                      <tr key={log.id}>
                        <td style={{ whiteSpace:'nowrap', fontSize:13, color:'var(--gray-600)' }}>
                          <div>{format(new Date(log.created_at), 'dd/MM/yyyy')}</div>
                          <div style={{ fontSize:11, color:'var(--gray-400)' }}>
                            {format(new Date(log.created_at), 'HH:mm:ss')}
                          </div>
                        </td>
                        <td style={{ fontSize:13, whiteSpace:'nowrap' }}>
                          {log.user_email || <span style={{ color:'var(--gray-400)' }}>—</span>}
                        </td>
                        <td>
                          {log.user_role && (
                            <span style={{
                              display:'inline-block', padding:'2px 8px', borderRadius:20,
                              fontSize:11, fontWeight:600,
                              color: roleStyle.color, background: roleStyle.bg,
                            }}>
                              {ROLE_LABELS[log.user_role] || log.user_role}
                            </span>
                          )}
                        </td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{
                              width:28, height:28, borderRadius:8, flexShrink:0,
                              background: cfg.bg,
                              display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                              <Icon size={14} color={cfg.color} strokeWidth={2} />
                            </div>
                            <span style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap' }}>
                              {log.action_label}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontSize:13, color:'var(--gray-600)', maxWidth:300 }} className="truncate">
                          {log.description || '—'}
                        </td>
                        <td style={{ fontSize:12, color:'var(--gray-400)', whiteSpace:'nowrap' }}>
                          {log.ip_address || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16, flexWrap:'wrap', gap:10 }}>
                <span style={{ fontSize:13, color:'var(--gray-500)' }}>
                  Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total.toLocaleString()} registros
                </span>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-secondary btn-sm"
                    disabled={page === 0}
                    onClick={() => handleSearch(page - 1)}
                    style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <ChevronLeft size={15} /> Anterior
                  </button>
                  <span style={{ display:'flex', alignItems:'center', padding:'0 12px', fontSize:13, color:'var(--gray-600)' }}>
                    Página {page + 1} de {totalPages}
                  </span>
                  <button className="btn btn-secondary btn-sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => handleSearch(page + 1)}
                    style={{ display:'flex', alignItems:'center', gap:4 }}>
                    Siguiente <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}